/**
 * Unified error handling utility for report creation/cloning operations.
 * This ensures consistent error messages across all report mutation components.
 */

export interface ReportErrorHandlerOptions {
  genericErrorMessage: string;
  onError?: (errorMessage: string) => void;
}

/**
 * Processes report operation errors and returns user-friendly error messages.
 * Handles specific database server errors like job limits and internal errors.
 */
export function getReportErrorMessage(
  error: Error,
  fallbackMessage: string,
): string {
  const errorMessage = error.message;

  // Handle database server job limit errors
  if (
    errorMessage.includes('maximum capacity') ||
    errorMessage.includes('maximum job capacity') ||
    errorMessage.includes('Maximum number of reports reached')
  ) {
    return errorMessage; // Use the user-friendly message from createSession.ts
  }

  // Handle database server internal errors
  if (errorMessage.includes('internal server error')) {
    return errorMessage; // Use the user-friendly message from createSession.ts
  }

  // Handle legacy job exceeded messages (for backward compatibility)
  if (errorMessage.includes('jobs exceeded')) {
    const match = errorMessage.match(/Limit of jobs exceeded: (\d+) of (\d+)/);
    if (match) {
      const [, current, total] = match;
      return `Maximum capacity for reports reached (${current}/${total} reports). Delete existing reports or contact administrator.`;
    }
    return 'Maximum capacity for reports reached. Delete existing reports or contact administrator.';
  }

  // Return the fallback message for all other errors
  return fallbackMessage;
}

/**
 * Creates a standardized error callback for report operations.
 * This ensures consistent error handling across all report components.
 */
export function createReportErrorCallback(
  options: ReportErrorHandlerOptions,
): (error: Error) => void {
  return (error: Error) => {
    const errorMessage = getReportErrorMessage(
      error,
      options.genericErrorMessage,
    );

    if (options.onError) {
      options.onError(errorMessage);
    }
  };
}
